import type { VideoPresentation } from './video-presentation.js';

export const PLATE_TRANSPARENCY_LIMITS = { min: 0, max: 100 } as const;
export interface PresentationAppearance {
  readonly plateTransparency?: number | undefined;
  readonly textColor?: string | undefined;
}

/** Older row layouts retain solid plates until an appearance is selected. */
export function presentationAppearance(value: VideoPresentation | undefined) {
  return { plateTransparency: value?.plateTransparency ?? (value?.version === 2 ? 20 : 0),
    textColor: value?.textColor ?? '#ffffff' };
}

export function appearanceFields(value: PresentationAppearance | undefined): PresentationAppearance {
  return { ...(value?.plateTransparency === undefined ? {} : { plateTransparency: value.plateTransparency }),
    ...(value?.textColor === undefined ? {} : { textColor: value.textColor }) };
}

export function withPresentationAppearance<T extends VideoPresentation>(value: T, appearance: PresentationAppearance | undefined): T {
  return { ...value, ...appearanceFields(appearance) } as T;
}

/** Interpolate the backing plates independently from opaque information text. */
export function interpolatePresentationAppearance(from: PresentationAppearance, to: PresentationAppearance, amount: number): PresentationAppearance {
  if (amount <= 0) return from;
  if (amount >= 1) return to;
  const transparency = from.plateTransparency === undefined && to.plateTransparency === undefined ? {} : {
    plateTransparency: (from.plateTransparency ?? 0) + ((to.plateTransparency ?? 0) - (from.plateTransparency ?? 0)) * amount };
  if (from.textColor === undefined && to.textColor === undefined) return transparency;
  const start = from.textColor ?? '#ffffff';
  const finish = to.textColor ?? '#ffffff';
  const textColor = '#' + [1, 3, 5].map(index => {
    const a = Number.parseInt(start.slice(index, index + 2), 16);
    const b = Number.parseInt(finish.slice(index, index + 2), 16);
    return Math.round(a + (b - a) * amount).toString(16).padStart(2, '0');
  }).join('');
  return { ...transparency, textColor };
}
